import React from 'react';
import { motion } from 'framer-motion';
import { PlayCircle, Smartphone, BookOpen, ShieldCheck, Clock, ArrowRight } from 'lucide-react';
import { Button, Countdown } from './ui';
const CHECKOUT_URL = 'https://sistema-iphone-ads.myshopify.com/cart/add?id=46176935641285&quantity=1&return_to=/cart';
const HeroVideo = ({
  className = ''
}: {
  className?: string;
}) => <div className={`relative w-full max-w-sm mx-auto aspect-[9/16] rounded-3xl shadow-xl md:shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden group border border-blue-500/20 bg-zinc-900 ${className}`} data-id="element-135">
    <video autoPlay muted loop playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" data-id="element-136">
      <source src="https://cdn.magicpatterns.com/uploads/c3MnEZ7pGSwvHZU1pZ7Ssb/ugly-ad-5_THMHkXmO.mp4" type="video/mp4" data-id="element-137" />
    </video>

    {/* Dark Overlays */}
    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent pointer-events-none" data-id="element-138"></div>
    <div className="absolute inset-0 bg-blue-900/10 mix-blend-overlay pointer-events-none" data-id="element-139"></div>

    {/* Top Badge */}
    <div className="absolute top-5 left-1/2 -translate-x-1/2 bg-zinc-900/90 backdrop-blur-md border border-zinc-700/50 rounded-full px-4 py-1.5 shadow-lg whitespace-nowrap z-10" data-id="element-140">
      <span className="text-xs font-bold text-zinc-200 uppercase tracking-wider" data-id="element-141">
        REFERENCIA REAL
      </span>
    </div>

    {/* Bottom Text */}
    <div className="absolute bottom-6 left-0 right-0 px-6 text-center z-10" data-id="element-142">
      <p className="text-sm font-medium text-white drop-shadow-md bg-zinc-950/60 backdrop-blur-sm py-2 px-3 rounded-xl border border-white/[0.05]" data-id="element-143">
        Así se ve aplicado en una tienda que ya trabaja con nosotros.
      </p>
    </div>
  </div>;
export const Hero = () => {
  return <section className="relative pt-20 pb-12 md:pt-32 md:pb-24 px-6 md:px-12 max-w-7xl mx-auto overflow-hidden" data-id="element-144">
      {/* Background Glow - Reduced on mobile */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] md:w-[800px] h-[400px] md:h-[800px] bg-blue-500/15 rounded-full blur-[60px] md:blur-[120px] -z-10 pointer-events-none" data-id="element-145" />

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-center" data-id="element-146">
        <motion.div initial={{
        opacity: 0,
        x: -30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6
      }} className="flex flex-col space-y-8" data-id="element-147">
          <div className="inline-flex items-center space-x-2 bg-zinc-900/80 border border-zinc-800 rounded-full px-4 py-2 w-fit" data-id="element-148">
            <span className="flex h-2 w-2 rounded-full bg-blue-500 animate-pulse" data-id="element-149"></span>
            <span className="text-sm font-medium text-zinc-300" data-id="element-150">
              Sistema iPhone Ads
            </span>
          </div>

          <p className="text-sm md:text-base font-semibold text-blue-300" data-id="element-151">
            Empezá sin stock, vendé por pedido y aprendé a conseguir clientes.
          </p>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1]" data-id="element-152">
            Antes de comprar tu primer iPhone,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600" data-id="element-153">
              aprendé cómo venderlo
            </span>
          </h1>

          <p className="text-lg md:text-xl text-zinc-400 leading-relaxed" data-id="element-154">
            Un curso práctico para entender el negocio de los iPhones, vender
            sin stock propio, evitar comprar mal y conseguir clientes usando
            Instagram, WhatsApp y Meta Ads.
          </p>

          {/* Compact steps line */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-zinc-300" data-id="element-155">
            <span data-id="element-156">Sin stock</span>
            <ArrowRight className="w-3.5 h-3.5 text-zinc-600" data-id="element-157" />
            <span data-id="element-158">Venta por pedido</span>
            <ArrowRight className="w-3.5 h-3.5 text-zinc-600" data-id="element-159" />
            <span data-id="element-160">Clientes por redes</span>
          </div>

          <div className="flex items-center text-sm md:text-base font-medium text-emerald-400" data-id="element-161">
            <ShieldCheck className="w-4 h-4 mr-2 flex-shrink-0" data-id="element-162" />
            <span data-id="element-163">
              Curso grabado · Acceso inmediato · Precio especial $19.000
            </span>
          </div>

          {/* Mobile Video: appears before price block */}
          <HeroVideo className="lg:hidden mt-2 mb-4" data-id="element-164" />

          <div className="bg-zinc-900/40 border border-white/[0.05] rounded-3xl p-6 md:p-8 backdrop-blur-sm md:backdrop-blur-md shadow-lg md:shadow-2xl relative overflow-hidden" data-id="element-165">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-emerald-500 to-blue-500" data-id="element-166"></div>

            <div className="flex flex-col space-y-6" data-id="element-167">
              <div data-id="element-168">
                <p className="text-sm text-blue-400 font-semibold uppercase tracking-wider mb-2" data-id="element-169">
                  Precio especial de lanzamiento
                </p>
                <div className="flex items-baseline space-x-3" data-id="element-170">
                  <span className="text-4xl md:text-5xl font-extrabold text-white" data-id="element-171">
                    $19.000
                  </span>
                  <span className="text-lg text-zinc-500 line-through" data-id="element-172">
                    $40.000
                  </span>
                </div>
                <p className="text-base font-semibold text-white mt-2" data-id="element-173">
                  Más de 50% OFF durante los próximos 5 minutos. Cuando termine
                  el contador, el acceso vuelve a $40.000.
                </p>
              </div>

              <Countdown data-id="element-174" />

              <div className="pt-2" data-id="element-175">
                <p className="text-center text-sm font-bold text-amber-300 mb-3" data-id="element-176">
                  Quedan pocos minutos para acceder a $19.000 antes de que
                  vuelva a $40.000.
                </p>
                <Button href={CHECKOUT_URL} className="w-full py-5 text-xl shadow-[0_0_30px_rgba(16,185,129,0.2)]" data-id="element-177">
                  Quiero acceder por $19.000
                </Button>
                <p className="text-xs text-center text-zinc-500 mt-4" data-id="element-178">
                  Si no accedés antes de que termine esta cuenta regresiva, el
                  precio vuelve a $40.000.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 pt-2" data-id="element-179">
            <div className="flex items-center text-sm text-zinc-400" data-id="element-180">
              <ShieldCheck className="w-4 h-4 mr-2 text-emerald-500" data-id="element-181" /> Acceso
              digital
            </div>
            <div className="flex items-center text-sm text-zinc-400" data-id="element-182">
              <PlayCircle className="w-4 h-4 mr-2 text-emerald-500" data-id="element-183" /> Curso
              grabado
            </div>
            <div className="flex items-center text-sm text-zinc-400" data-id="element-184">
              <BookOpen className="w-4 h-4 mr-2 text-emerald-500" data-id="element-185" /> Clases +
              materiales prácticos
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="relative lg:h-[600px] hidden lg:flex items-center justify-center mt-8 lg:mt-0" data-id="element-186">
          {/* Desktop Video: replaces the static image */}
          <HeroVideo className="max-w-md" data-id="element-187" />
        </motion.div>
      </div>
    </section>;
};